import React, { useMemo } from 'react';

import { sanitiseId } from '../../helpers/sanitiseId';
import { ChunkedImageProperties } from '../../types/chunkedImage';
import { LengthRange } from '../../types/lengthRange';

import { CoreLaneRowAlignment, CoreLaneRowConfiguration, CoreLaneRowType } from './CoreLaneRows';
import { ImageLaneRow } from './CoreLaneRows/ImageLaneRow';
import { IntervalLaneRow } from './CoreLaneRows/IntervalLaneRow';
import { NumericLaneRow } from './CoreLaneRows/NumericPointsLaneRow';
import { RulerLaneRow } from './CoreLaneRows/RulerLaneRow';

export interface CoreLaneProps {
	id: string;
	rows: (CoreLaneRowConfiguration & { height: number })[];
	width: number;
	scale: number;
	lane: LengthRange;
	visibleRange: LengthRange;
	overviewChunks?: ChunkedImageProperties[];
	laneHeightInPx: number;
	allowableRanges?: LengthRange[];
	crosshairDepthM?: number;
}

type CoreLaneRowConfigurationWithHeightAndPosition = CoreLaneRowConfiguration & {
	height: number;
	position: number;
};

const CoreLane: React.FC<CoreLaneProps> = ({
	id,
	rows,
	width,
	scale,
	lane,
	visibleRange,
	laneHeightInPx,
	allowableRanges,
	crosshairDepthM,
}) => {
	const rowsWithPositioning = useMemo(
		() =>
			[
				// Top
				rows
					.filter((r) => r.alignment === CoreLaneRowAlignment.Top)
					.reduce<CoreLaneRowConfigurationWithHeightAndPosition[]>((prev, current, currentIndex) => {
						const prevItem = currentIndex ? prev[currentIndex - 1] : undefined;
						return [...prev, { ...current, position: prevItem ? prevItem.position + prevItem.height : 0 }];
					}, []),
				// Centre
				rows
					.filter((r) => r.alignment === CoreLaneRowAlignment.Centre || r.alignment === undefined)
					// Given we are going from the bottom up, we have to reverse the order
					.reduce<CoreLaneRowConfigurationWithHeightAndPosition[]>((prev, current, currentIndex, src) => {
						const prevItem = currentIndex ? prev[currentIndex - 1] : undefined;
						const totalHeight = rows.reduce((prev, curr) => prev + curr.height, 0);
						const topHeight = rows
							.filter((r) => r.alignment === CoreLaneRowAlignment.Top)
							.reduce((prev, curr) => prev + curr.height, 0);
						const spacing = (laneHeightInPx - totalHeight) / (2 + (src.length - 1));
						return [
							...prev,
							{
								...current,
								position: prevItem
									? prevItem.position + prevItem.height + spacing
									: topHeight + spacing,
							},
						];
					}, []),
				// Bottom
				rows
					.filter((r) => r.alignment === CoreLaneRowAlignment.Bottom)
					// Given we are going from the bottom up, we have to reverse the order
					.reverse()
					.reduce<CoreLaneRowConfigurationWithHeightAndPosition[]>((prev, current, currentIndex) => {
						const prevItem = currentIndex ? prev[currentIndex - 1] : undefined;
						return [
							...prev,
							{
								...current,
								position: prevItem
									? prevItem.position - current.height
									: laneHeightInPx - current.height,
							},
						];
					}, []),
			].reduce((prev, curr) => [...prev, ...curr], []),
		[laneHeightInPx, rows],
	);

	return (
		<>
			{rowsWithPositioning.map((rowConfig, idx) => {
				if (!rowConfig.height) return undefined;
				const sanitizedConfigurationId = sanitiseId(rowConfig.id);
				const rowId = `${id}--${sanitizedConfigurationId}--image-${idx}`;
				switch (rowConfig.type) {
					case CoreLaneRowType.Image:
						return (
							<ImageLaneRow
								key={rowId}
								id={rowId}
								multiplier={1}
								width={width}
								scale={scale}
								lane={lane}
								height={rowConfig.height}
								position={rowConfig.position}
								configuration={rowConfig}
								visibleLanesRange={visibleRange}
								crosshairDepthM={crosshairDepthM}
							/>
						);
					case CoreLaneRowType.Interval:
						return (
							<IntervalLaneRow
								key={rowId}
								id={rowId}
								width={width}
								scale={scale}
								lane={lane}
								configuration={rowConfig}
								position={rowConfig.position}
								heightInPx={rowConfig.height}
								allowableEditRanges={allowableRanges}
							/>
						);
					case CoreLaneRowType.Ruler:
						return (
							<RulerLaneRow
								key={rowId}
								id={rowId}
								position={rowConfig.position}
								lane={lane}
								scale={scale}
								heightInPx={rowConfig.height}
								configuration={rowConfig}
								width={width}
							/>
						);
					case CoreLaneRowType.Numeric:
						return (
							<NumericLaneRow
								key={rowId}
								id={rowId}
								configuration={rowConfig}
								width={width}
								scale={scale}
								lane={lane}
								position={rowConfig.position}
								heightInPx={rowConfig.height}
							></NumericLaneRow>
						);
				}
			})}
		</>
	);
};

export default CoreLane;
